<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, onMounted, onUpdated, ref, watch, nextTick } from 'vue';
import type { TimelineConfig } from '../../types/timeline';
import TextMediaElement from '../TextMediaElement/TextMediaElement.vue';
import { sortTimelineConfig, getYear } from './helpers';

const props = defineProps({
  config: {
    type: Object as () => TimelineConfig,
    default: () => {},
  },
});

const buffer: number = 10;
const shouldGetSorted: boolean = false;

const windowWidth = ref(window.innerWidth);
const sortedTimeline = ref<TimelineConfig | null>(null);
const timelineLineRef = ref<HTMLElement | null>(null);
const decadesContainerRef = ref<HTMLElement | null>(null);
const sectionsArray = ref<Element[] | null>(null);
const lineOffsetTop = ref(0);
const lineOffsetBottom = ref(0);
const lineHeight = ref(0);
const pageRef = ref<HTMLElement | null>(document.documentElement);
const shouldBeSticky = ref<boolean>(false);
const pageHeight = ref<number>(pageRef.value ? pageRef.value.scrollHeight : 0);
const pageHeader = ref<HTMLElement | undefined>(undefined);
const stickyNav = ref<HTMLElement | null>(null);
const decadeRef = ref<HTMLElement[] | null>([]);
const offset = ref<number>(0);
const spyOffset = ref<number>(0);
const scrollPositionRecord = ref(document.body.getBoundingClientRect().top);
const currentScrollPosition = ref(0);
const isScrollingUp = ref(false);
const isScrollingDown = ref(false);
const startScrollArea = ref<number | null>(null);
const endScrollArea = ref<number | null>(null);
const activeNavLink = ref<HTMLAnchorElement | null>(null);
const gap = ref<number | null>(null);

let observer: IntersectionObserver | null = null;

const lineProperties = () => {
  const line = timelineLineRef.value;
  if (!line) {
    return;
  }
  lineOffsetTop.value = line.getBoundingClientRect().top;
  lineHeight.value = pageHeight.value;
  lineOffsetBottom.value = lineHeight.value - line.getBoundingClientRect().bottom;

  line.style.top = `-${lineOffsetTop.value}px`;
  line.style.height = `${lineHeight.value}px`;
  line.style.bottom = `-${lineOffsetBottom.value}px`;
};

const getHeaderComponent = () => {
  pageHeader.value =
    (document.getElementsByClassName('.PageHeader1XP__scrollIntoView')[0] as HTMLElement) ||
    document.getElementById('pageHeader')?.childNodes[0];
};

const handleResize = () => {
  windowWidth.value = window.innerWidth;
  setOffsets();
  updateStickyState();
};

const handleScroll = () => {
  initScrollSpy();
  updateStickyState();
};

const setOffsets = () => {
  const bodyTag = document.body;
  getHeaderComponent();
  offset.value = pageHeader.value ? pageHeader.value.offsetHeight : 0;

  currentScrollPosition.value = bodyTag.getBoundingClientRect().top;
  isScrollingUp.value =
    Math.abs(currentScrollPosition.value) < Math.abs(scrollPositionRecord.value);
  isScrollingDown.value =
    Math.abs(currentScrollPosition.value) > Math.abs(scrollPositionRecord.value);

  if (isScrollingUp.value) {
    if (stickyNav.value) {
      stickyNav.value.style.top = `${offset.value}px`;
      spyOffset.value = offset.value + stickyNav.value.offsetHeight;
    }
  } else {
    if (stickyNav.value) {
      stickyNav.value.style.top = '0px';
      spyOffset.value = stickyNav.value.offsetHeight;
    }
  }

  if (isScrollingDown.value === isScrollingUp.value) {
    return;
  }

  scrollPositionRecord.value = bodyTag.getBoundingClientRect().top;
};

const initScrollSpy = () => {
  setOffsets();

  if (!sectionsArray.value?.length) {
    return;
  }

  for (const section in sectionsArray.value) {
    if (stickyNav.value) {
      const node = stickyNav.value.children[0].children[section] as HTMLElement;
      const parent = node.parentElement as HTMLElement;
      const allSiblings = parent.children;

      allSiblings &&
        Array.from(allSiblings).forEach((sibling) => {
          if (sibling.children[0] instanceof HTMLElement) {
            sibling.children[0].blur();
          }
        });

      const elementBounding = sectionsArray.value[section].getBoundingClientRect();

      if (
        elementBounding.top <= spyOffset.value + buffer &&
        elementBounding.bottom >= spyOffset.value + buffer
      ) {
        node?.children[0].classList.add('Timeline__activeLink');
      } else {
        node?.children[0].classList.remove('Timeline__activeLink');
      }
    }
  }
};

const activeStyling = (anchorLink) => {
  if (!anchorLink) {
    return;
  }

  const parent = anchorLink.closest('ul');

  parent &&
    parent.querySelector('.Timeline__activeLink') &&
    parent.querySelector('.Timeline__activeLink')?.classList.remove('Timeline__activeLink');

  return anchorLink.classList.add('Timeline__activeLink');
};

const updateHash = (id: string) => {
  const url = new URL(window.location.href);
  url.hash = id;
  history.replaceState(null, '', url.toString());
};

const scrollToPosition = (id: string, e?: Event) => {
  if (e) {
    e.preventDefault();
  }

  setOffsets();

  if (!decadeRef.value) {
    return;
  }

  const anchorTarget = decadeRef.value.find((anchor) => anchor.id === id);
  activeNavLink.value = stickyNav.value!.querySelector<HTMLAnchorElement>(`a[href="#${id}"]`);

  if (anchorTarget) {
    // timeOut is needed to let the header show on scroll up & recalculate the scroll position of the target
    setTimeout(() => {
      const anchorPosition = anchorTarget.getBoundingClientRect().top + window.scrollY + buffer;
      const isNewPositionHigher = Math.abs(scrollPositionRecord.value) > anchorPosition;
      const scrollPosition = isNewPositionHigher
        ? anchorPosition - (stickyNav.value?.offsetHeight || 0) - (offset.value || 0)
        : anchorPosition - (stickyNav.value?.offsetHeight || 0);

      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }, 50);
  }

  updateHash(id);
};

const initScrollSpyObserver = () => {
  if (!decadeRef.value || !stickyNav.value) {
    return;
  }

  observer?.disconnect();

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const id = entry.target.id;
        activeNavLink.value = stickyNav.value!.querySelector<HTMLAnchorElement>(`a[href="#${id}"]`);
        if (activeNavLink.value) {
          if (entry.isIntersecting) {
            gap.value =
              Number(
                getComputedStyle(
                  decadeRef.value?.find((item) => item.id === id)?.parentElement as Element,
                )?.gap?.replace('px', ''),
              ) + buffer;
            debounceHashUpdate(id);
          }
        }
      });
    },
    {
      rootMargin: `-${(stickyNav.value?.offsetHeight || 0) + buffer}px 0px ${gap.value || 0}px 0px`,
      threshold: 0.1,
    },
  );

  decadeRef.value.forEach((el) => observer?.observe(el));
};

let hashTimeout: ReturnType<typeof setTimeout> | null = null;
const debounceHashUpdate = (id: string, delay = 100) => {
  if (hashTimeout) clearTimeout(hashTimeout);
  hashTimeout = setTimeout(() => updateHash(id), delay);
};

const updateStickyState = () => {
  const scrollTop = window.scrollY;

  if (!decadesContainerRef.value) {
    return;
  }

  const rect = decadesContainerRef.value.getBoundingClientRect();
  startScrollArea.value = scrollTop + rect.top - (isScrollingUp.value ? offset.value : 0);
  endScrollArea.value =
    startScrollArea.value + rect.height - (stickyNav.value ? stickyNav.value?.offsetHeight : 0);

  shouldBeSticky.value =
    scrollTop >= startScrollArea.value - (stickyNav.value?.offsetHeight || 0) &&
    scrollTop <= endScrollArea.value;

  if (stickyNav.value) {
    if (shouldBeSticky.value) {
      decadesContainerRef.value.style.marginTop = `${stickyNav.value?.offsetHeight}px`;
    } else {
      decadesContainerRef.value.style.marginTop = '0px';
    }
  }
};

watch(
  () => props.config,
  (newConfig) => {
    if (newConfig) {
      sortedTimeline.value = shouldGetSorted ? sortTimelineConfig(newConfig) : newConfig;
    }
  },
  { immediate: true },
);

watch(
  windowWidth,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      pageHeight.value = pageRef.value ? pageRef.value.scrollHeight : 0;
      if (timelineLineRef.value && pageHeight.value !== lineHeight.value) {
        lineProperties();
      }
    }
  },
  { immediate: true },
);

watch(
  pageHeight,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      if (timelineLineRef.value && newValue !== lineHeight.value) {
        lineProperties();
      }
    }
  },
  { immediate: true },
);

watch(
  pageHeader,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      setOffsets();
    }
  },
  { immediate: true },
);

watch(activeNavLink, (newValue) => {
  activeStyling(newValue);
});

onBeforeMount(() => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll);
  window.addEventListener('headerMounted', getHeaderComponent);
});

onMounted(async () => {
  await nextTick();
  updateStickyState();

  const hash = window.location.hash.replace('#', '');
  if (hash && stickyNav.value) {
    activeNavLink.value = stickyNav.value.querySelector<HTMLAnchorElement>(`a[href="#${hash}"]`);
    scrollToPosition(hash);
  }

  const sectionsList = decadesContainerRef.value?.querySelectorAll('.Timeline__decadeEntry');

  sectionsArray.value = sectionsList ? Array.from(sectionsList) : null;
  setOffsets();

  // to let some time for all other page elements to load
  setTimeout(() => {
    pageHeight.value = pageRef.value ? pageRef.value.scrollHeight : 0;
  }, 500);

  if (decadeRef.value) {
    initScrollSpyObserver();
  }
});

onUpdated(() => {
  pageHeight.value = pageRef.value ? pageRef.value.scrollHeight : 0;
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('headerMounted', getHeaderComponent);
  observer?.disconnect();
});
</script>

<template v-if="sortedTimeline && sortedTimeline.decades && sortedTimeline.decades.length">
  <div :class="$style.container">
    <div :class="$style.header">
      <h3 v-if="config.title">{{ config.title }}</h3>
      <nav ref="stickyNav" :class="[$style.scrollNavigation, shouldBeSticky && $style.isSticky]">
        <ul>
          <li v-for="decadeEntry in sortedTimeline?.decades" :key="decadeEntry.decade">
            <a
              :href="`#decade-${decadeEntry.decade}`"
              @click="
                (e) => {
                  scrollToPosition(`decade-${decadeEntry.decade}`, e);
                }
              "
              >{{ decadeEntry.decade }}</a
            >
          </li>
        </ul>
      </nav>
    </div>
    <div ref="decadesContainerRef" :class="$style.decadesContainer">
      <div :class="$style.lineContainer">
        <div ref="timelineLineRef" :class="$style.timelineLine" />
      </div>
      <div
        v-for="decadeEntry in sortedTimeline?.decades"
        :key="decadeEntry.decade"
        :class="$style.decadeEntry"
      >
        <h4 :id="`decade-${decadeEntry.decade}`" ref="decadeRef" :class="$style.decadeTitle">
          <span>{{ decadeEntry.title }}</span>
        </h4>
        <template v-for="(dateEntry, index) in decadeEntry.dates" :key="dateEntry.date + index">
          <!-- eslint-disable max-len -->
          <TextMediaElement
            :badge="getYear(dateEntry.date).toString()"
            :class="$style.dateEntry"
            :image="
              (dateEntry.media?.mediaType === 'video' && dateEntry.media?.thumbnail
                ? dateEntry.media.thumbnail
                : undefined) ||
              (dateEntry.media?.mediaType === 'image' && dateEntry.media?.src
                ? dateEntry.media?.src
                : undefined)
            "
            :image-alt="
              dateEntry.media && dateEntry.media.mediaType === 'image'
                ? dateEntry.media.alt
                : undefined
            "
            :is-timeline="true"
            :media-left="dateEntry.media?.mediaPosition === 'left'"
            :media-right="dateEntry.media?.mediaPosition === 'right'"
            :text="dateEntry.text"
            :title="dateEntry.title"
            :video="
              dateEntry.media?.mediaType === 'video' &&
              !dateEntry.media?.isYouTube &&
              dateEntry.media?.src
                ? dateEntry.media.src
                : undefined
            "
            :youtube="
              dateEntry.media?.mediaType === 'video' &&
              dateEntry.media?.isYouTube &&
              dateEntry.media?.src
                ? dateEntry.media.src
                : undefined
            "
          />
          <!-- eslint-enable max-len -->
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@forward './Timeline.scss';
</style>
